// @givmo/app-charity — the claim wizard's memberships REFRESH probe (CPA-WEB55).
//
// ══ WHAT IT IS FOR ═════════════════════════════════════════════════════════════════════════════
//
// The wizard's entry decides its held-membership short-circuit (CPA-WEB52) from the active-org
// provider's list, and that list carries a 15s stale window (`MEMBERSHIPS_STALE_TIME`). An Owner
// installed ELSEWHERE inside that window — a colleague adding them while they hold the wizard's
// address — was shown the attestation form for an organization they already administer. The backend
// answers such a re-submit idempotently, so it is friction rather than a consent defect, but it is
// the same staleness the organization deep link already cures, and it takes the same cure: force ONE
// refresh of the ONE memberships query when the address is opened, and let the door wait for it.
//
// ══ WHY THE REFRESH LIVES HERE AND NOT IN THE ENTRY ════════════════════════════════════════════
//
// ⚠ THIS IS THE WHOLE REASON THE FILE EXISTS, AND IT WAS MEASURED, NOT ASSUMED. `ClaimWizardEntry`
// renders under `<PortalEntryGate>` and `<OrgScopedBoundary>`, and BOTH remount it:
//
//   · the gate SWAPS THE CHROME when the memberships read settles — the onboarding frame for a
//     caller who administers nothing, the portal shell otherwise — and the swap remounts the wizard
//     (it is the very phenomenon `router/claim-wizard-terminal.test.tsx` exists for);
//   · the boundary RE-KEYS on every change of the active organization, including the cold-load
//     change from "no organization yet" to the provider's default first membership, and door 1's
//     own selection.
//
// A `refetchOnMount: 'always'` observer inside the entry is therefore asked again by each fresh
// instance. Measured on the ordinary visitor — a caller who administers nothing, so the gate swaps:
// TWO memberships requests where the row promises one. Worse than the count, the second answer lands
// while the person is filling the form, so a membership that appears in it forwards them to the
// console mid-ceremony.
//
// So the refresh is mounted ONCE by the authenticated layout, beside `<MembershipsFreshness>` and
// `<OrganizationDeepLinkProbe>` — above the gate and above the boundary, where it survives every
// remount it may cause. That is the same placement, for the same reason, CPA-BE29 established for
// the deep link (`router/organization-deep-link.tsx`, § WHY THE PROBE LIVES IN THE LAYOUT).
//
// The entry cannot move up with it: the entry PAINTS (the loading card, the refusal panel, the
// ceremony). So the two split the work — this probe asks and remembers, the entry waits and decides
// — and the answer crosses between them through the query cache under
// `claimMembershipsRefreshedKey`, which is the same idiom the entry's door 0 already uses for the
// cached terminal result (a `skipToken` observer over a key nothing fetches).
//
// ⚠ IF THIS PROBE IS NOT MOUNTED, THE WIZARD NEVER LEAVES ITS LOADING CARD — the entry waits for an
// answer that never comes. That is loud rather than silent: every wizard test in
// `router/claim-wizard-entry.test.tsx` drives the real route tree, so dropping the mount in
// `router/routes.tsx` reds all of them at once.
//
// ══ THE DECISION ═══════════════════════════════════════════════════════════════════════════════
//
//   1. SKIPPED, captured at entry, in two cases — which is what holds this to ONE request and no
//      delay where none is owed:
//        · a cached TERMINAL RESULT outranks door 1 entirely (CPA-WEB51), so a claimant reading
//          their own result must not spend a request on a question that cannot change what they see;
//        · a list that ALREADY holds this charity has nothing to discover — "the refresh exists to
//          find a membership the cache does NOT hold" (the deep link's words for its own guard).
//      A skipped probe publishes `true` immediately, so the entry never waits for it.
//   2. OTHERWISE ONE REFRESH, and `isFetchedAfterMount` is the fact published: it flips only once
//      the query has answered — with data OR an error — after this probe mounted. An errored refresh
//      therefore RELEASES the door rather than hanging it, which is what the entry already does for
//      any memberships read it cannot trust.
//   3. LEAVING THE ADDRESS FORGETS THE ANSWER. The published key is removed on unmount, so a later
//      visit to the same wizard refreshes again rather than deciding on the first visit's answer.
//      (Keyed by `charityId`, so moving between two wizards is a leave and an arrival.)

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { membershipsQueryOptions, useActiveOrg } from '../../org/active-org-context';
import { claimTerminalResultKey } from './claim-terminal-result';

/**
 * Where the probe publishes its one fact and the entry reads it. Nothing fetches this key — it is
 * written with `setQueryData` and read with a `skipToken` observer, exactly as the wizard's cached
 * terminal result is.
 */
export function claimMembershipsRefreshedKey(charityId: string): readonly string[] {
  return ['claim-wizard-memberships-refreshed', charityId];
}

export interface ClaimWizardRefreshProbeProps {
  /** The matched wizard's `$charityId`, or `null` while the address is not the one matched. */
  charityId: string | null;
}

/**
 * Mounted ONCE by the authenticated layout (see the header). Renders one refresh per wizard address,
 * keyed by the charity id, and nothing at every other address.
 */
export function ClaimWizardRefreshProbe({ charityId }: ClaimWizardRefreshProbeProps) {
  if (charityId === null) return null;
  return <ClaimMembershipsRefresh key={charityId} charityId={charityId} />;
}

function ClaimMembershipsRefresh({ charityId }: { charityId: string }) {
  const queryClient = useQueryClient();
  const { memberships } = useActiveOrg();

  // Captured at ENTRY: what is true when the address is opened decides whether anything is asked.
  const [skip] = useState(
    () =>
      queryClient.getQueryData(claimTerminalResultKey(charityId)) !== undefined ||
      memberships.some((membership) => membership.charity_id === charityId),
  );

  const { isFetchedAfterMount } = useQuery({
    // ⚠ SPREAD, never the bare generated factory: React Query runs whichever observer set the
    // query's options last, and it is the provider's own `queryFn` that makes an off-contract 200 an
    // error rather than an absence (CPA-WEB48).
    ...membershipsQueryOptions(),
    refetchOnMount: skip ? false : 'always',
    notifyOnChangeProps: ['isFetchedAfterMount'],
  });
  const refreshed = skip || isFetchedAfterMount;

  useEffect(() => {
    queryClient.setQueryData(claimMembershipsRefreshedKey(charityId), refreshed);
  }, [charityId, queryClient, refreshed]);

  // Leaving the address forgets the answer (step 3).
  useEffect(
    () => () => {
      queryClient.removeQueries({ queryKey: claimMembershipsRefreshedKey(charityId) });
    },
    [charityId, queryClient],
  );

  return null;
}
